import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
}

export const CURRENCIES: CurrencyInfo[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'MXN', symbol: 'Mex$', name: 'Mexican Peso' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Zloty' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso' },
  { code: 'VND', symbol: '₫', name: 'Vietnamese Dong' },
  { code: 'TWD', symbol: 'NT$', name: 'Taiwan Dollar' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'FIM', symbol: 'mk', name: 'Finnish Markka' },
  { code: 'IEP', symbol: '£', name: 'Irish Pound' },
  { code: 'ATS', symbol: 'öS', name: 'Austrian Schilling' },
  { code: 'BEF', symbol: 'fr', name: 'Belgian Franc' },
  { code: 'PTE', symbol: 'Esc', name: 'Portuguese Escudo' },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna' },
  { code: 'ARS', symbol: '$', name: 'Argentine Peso' },
  { code: 'CLP', symbol: '$', name: 'Chilean Peso' },
  { code: 'COP', symbol: 'Col$', name: 'Colombian Peso' },
  { code: 'PEN', symbol: 'S/', name: 'Peruvian Sol' },
  { code: 'EGP', symbol: '£', name: 'Egyptian Pound' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' },
  { code: 'ITL', symbol: '₤', name: 'Italian Lira' },
  { code: 'ESP', symbol: '₧', name: 'Spanish Peseta' },
  { code: 'NLG', symbol: 'ƒ', name: 'Dutch Guilder' },
  { code: 'FRF', symbol: '₣', name: 'French Franc' },
  { code: 'DEM', symbol: 'DM', name: 'German Mark' },
];

interface CurrencyContextValue {
  currency: string;
  setCurrency: (code: string) => void;
  rates: Record<string, number>;
  loading: boolean;
  error: string | null;
  convert: (amount: number, from: string, to: string) => number;
  formatAmount: (amount: number, currencyCode?: string) => string;
  refreshRates: () => void;
  currencySymbol: string;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

const STORAGE_KEY = 'selected-currency';
const RATES_CACHE_KEY = 'currency-rates-cache';
const CACHE_DURATION = 1000 * 60 * 60; // 1 hour

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<string>(() => {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY) || 'USD';
    }
    return 'USD';
  });
  const [rates, setRates] = useState<Record<string, number>>({ USD: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Check cache first
      if (typeof localStorage !== 'undefined') {
        const cached = localStorage.getItem(RATES_CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION) {
            setRates(data);
            setLoading(false);
            return;
          }
        }
      }

      const res = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      if (!res.ok) throw new Error('Failed to fetch exchange rates');
      const data = await res.json();
      const newRates = { ...data.rates, USD: 1 };

      setRates(newRates);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ data: newRates, timestamp: Date.now() }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      // Fallback to common static rates if fetch fails
      const fallbackRates: Record<string, number> = {
        USD: 1, EUR: 0.92, GBP: 0.79, JPY: 149.5, AUD: 1.52, CAD: 1.36, CHF: 0.88,
        CNY: 7.24, HKD: 7.82, INR: 83.3, SGD: 1.34, BRL: 4.95, KRW: 1335, MXN: 17.05,
        ZAR: 18.5, AED: 3.67, SAR: 3.75, SEK: 10.6, NOK: 10.8, PLN: 4.05, TRY: 32.5,
        RUB: 92.5, THB: 35.8, IDR: 15820, PHP: 56.2, VND: 24340, TWD: 31.8, MYR: 4.68,
        NZD: 1.64, DKK: 6.87, CZK: 23.2, ARS: 820, CLP: 890, COP: 4100, PEN: 3.75,
        EGP: 30.9, NGN: 1560, KES: 143,
      };
      setRates(fallbackRates);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const setCurrency = useCallback((code: string) => {
    setCurrencyState(code);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, code);
    }
  }, []);

  const convert = useCallback((amount: number, from: string, to: string): number => {
    if (from === to) return amount;
    const fromRate = rates[from] || 1;
    const toRate = rates[to] || 1;
    // amount is in `from` currency; convert to USD then to `to`
    return (amount / fromRate) * toRate;
  }, [rates]);

  const formatAmount = useCallback((amount: number, currencyCode?: string): string => {
    const code = currencyCode || currency;
    const isJPY = code === 'JPY' || code === 'KRW' || code === 'VND' || code === 'IDR' || code === 'CLP' || code === 'ARS' || code === 'COP';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: isJPY ? 0 : 2,
        maximumFractionDigits: isJPY ? 0 : 2,
      }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${code}`;
    }
  }, [currency]);

  const refreshRates = useCallback(() => {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(RATES_CACHE_KEY);
    }
    fetchRates();
  }, [fetchRates]);

  const currencySymbol = CURRENCIES.find(c => c.code === currency)?.symbol || '$';

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates, loading, error, convert, formatAmount, refreshRates, currencySymbol }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within a CurrencyProvider');
  return ctx;
}
